import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li', 'h2', 'h3', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style'];
const SAFE_URI_PATTERN = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;
const BLOCK_TAGS = new Set(['p', 'h2', 'h3', 'ul', 'ol', 'li']);

let hooksConfigured = false;

function configureHooks() {
  if (hooksConfigured) return;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }

    if (node.hasAttribute?.('style')) {
      const color = node.style?.color || '';
      const fontWeight = node.style?.fontWeight || '';
      const fontStyle = node.style?.fontStyle || '';
      const safeColor =
        /^#[0-9a-f]{6}$/i.test(color) || /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(color);
      const safeFontWeight = /^(bold|bolder|[6-9]00)$/i.test(fontWeight);
      const safeFontStyle = /^italic$/i.test(fontStyle);
      const safeStyles = [];

      if (safeColor) safeStyles.push(`color: ${color}`);
      if (safeFontWeight) safeStyles.push(`font-weight: ${fontWeight}`);
      if (safeFontStyle) safeStyles.push(`font-style: ${fontStyle}`);

      if (safeStyles.length) {
        node.setAttribute('style', `${safeStyles.join('; ')};`);
      } else {
        node.removeAttribute('style');
      }
    }
  });

  hooksConfigured = true;
}

export function sanitizeEmailHtml(html = '') {
  configureHooks();

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_PATTERN,
  });
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function isSafeHref(value = '') {
  return /^(https?:\/\/|mailto:|tel:)/i.test(String(value || '').trim());
}

function hasBoldStyle(style = '') {
  return /font-weight\s*:\s*(bold|bolder|[6-9]00)/i.test(style);
}

function hasItalicStyle(style = '') {
  return /font-style\s*:\s*italic/i.test(style);
}

function getSafeTagName(tagName = '') {
  const normalized = tagName.toLowerCase();

  if (normalized === 'h1') return 'h2';
  if (ALLOWED_TAGS.includes(normalized)) return normalized;

  return '';
}

function serializePastedNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const tag = getSafeTagName(node.tagName);

  if (tag === 'script' || tag === 'style' || tag === 'iframe') return '';
  if (tag === 'br') return '<br>';

  let children = Array.from(node.childNodes).map(serializePastedNode).join('');
  const style = node.getAttribute('style') || '';

  if (!children.trim() && BLOCK_TAGS.has(tag)) {
    children = '<br>';
  }

  if (!tag) {
    if (hasItalicStyle(style)) children = `<em>${children}</em>`;
    if (hasBoldStyle(style)) children = `<strong>${children}</strong>`;
    return children;
  }

  if (tag === 'a') {
    const href = node.getAttribute('href') || '';
    const hrefAttribute = isSafeHref(href) ? ` href="${escapeHtml(href)}"` : '';
    return `<a${hrefAttribute} target="_blank" rel="noopener noreferrer">${children}</a>`;
  }

  let content = children;
  if (!['strong', 'b'].includes(tag) && hasBoldStyle(style)) content = `<strong>${content}</strong>`;
  if (!['em', 'i'].includes(tag) && hasItalicStyle(style)) content = `<em>${content}</em>`;

  return `<${tag}>${content}</${tag}>`;
}

export function normalizeEmailClipboardHtml(html = '') {
  if (!html.trim()) return '';

  const parser = new DOMParser();
  const document = parser.parseFromString(html, 'text/html');
  const normalizedHtml = Array.from(document.body.childNodes).map(serializePastedNode).join('');

  return sanitizeEmailHtml(normalizedHtml);
}

export function plainTextToEmailHtml(text = '') {
  const lines = String(text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');

  const html = lines
    .map((line) => {
      const trimmed = line.trim();
      return trimmed ? `<p>${escapeHtml(trimmed)}</p>` : '<p><br></p>';
    })
    .join('');

  return sanitizeEmailHtml(html);
}
